/**
 * TalkGroupsPage
 *
 * Talk groups management page (May 2026 Figma redesign).
 *
 *   - Section toolbar: search icon-button, "Talk groups" + "N TOTAL"
 *     subtitle, overflow (...) button, "+ Add group" pill, view-toggle.
 *   - Table columns: select / TALK GROUP NAME / TEAM (pill) / SITES /
 *     MEMBERS / edit-and-delete actions.
 *
 * Per-row data:
 *   • TEAM column shows a pill — defaulted to the talk group name (backend
 *     has no "team" field yet; surfaced as a pill so the UI matches Figma).
 *   • SITES column shows comma-separated site names when the group has
 *     specific manual sites, otherwise the literal "Assigned to checkout".
 *   • MEMBERS column shows a per-group member count derived from the users
 *     API (groups by homeTalkGroupId + extraTalkGroupIds).
 *
 * Add / Edit modal lives in components/talk-groups/TalkGroupModal.jsx and
 * follows the same `.os-*` shell as the Site / Organization settings modals.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import organizationService from '../services/organizationService';
import userService from '../services/userService';
import { useTenant } from '../context/TenantContext';
import { toast } from '../components/Toast';
import TalkGroupModal from '../components/talk-groups/TalkGroupModal';

const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons`;
const CHK_ICON = `${ICON_BASE}/checkouts-page`;

const COLUMNS = [
  { id: 'name',    label: 'TALK GROUP NAME' },
  { id: 'team',    label: 'TEAM' },
  { id: 'sites',   label: 'SITES' },
  { id: 'members', label: 'MEMBERS' },
];

function siteIdOf(s) { return s?.siteId ?? s?.id; }
function siteNameOf(s) { return s?.siteName || s?.name || `Site ${siteIdOf(s)}`; }

function TalkGroupsPage() {
  const { orgId, currentOrg, sites } = useTenant();
  const [groups, setGroups]         = useState([]);
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [query, setQuery]           = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort]             = useState({ key: 'name', asc: true });
  const [selected, setSelected]     = useState(() => new Set());

  /* -------- Load ---------------------------------------------------------- */
  const load = useCallback(async () => {
    if (!orgId) {
      setGroups([]); setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const [grpRes, userRes] = await Promise.allSettled([
        organizationService.getTalkGroups(orgId),
        userService.listUsers(orgId, 0, 500),
      ]);
      const unwrap = (r) => {
        if (r.status !== 'fulfilled') return [];
        const d = r.value;
        const list = Array.isArray(d) ? d : (d?.content ?? d?.items ?? d?.data ?? []);
        return Array.isArray(list) ? list : [];
      };
      setGroups(unwrap(grpRes));
      setUsers(unwrap(userRes));
      if (grpRes.status === 'rejected') {
        toast.error(grpRes.reason?.response?.data?.message
          || grpRes.reason?.message
          || 'Failed to load talk groups');
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  /* -------- Per-group member counts --------------------------------------- */
  const memberCounts = useMemo(() => {
    const m = new Map();
    const bump = (k) => { if (k != null) m.set(k, (m.get(k) || 0) + 1); };
    users.forEach((u) => {
      bump(u.homeTalkGroupId);
      if (Array.isArray(u.extraTalkGroupIds)) u.extraTalkGroupIds.forEach(bump);
      if (Array.isArray(u.talkGroupIds))      u.talkGroupIds.forEach(bump);
    });
    return m;
  }, [users]);

  const siteById = useMemo(() => {
    const m = new Map();
    sites.forEach((s) => m.set(siteIdOf(s), s));
    return m;
  }, [sites]);

  /* -------- Rows ---------------------------------------------------------- */
  const rows = useMemo(() => groups.map((g) => {
    const id = g.talkGroupId ?? g.id;
    // Backend has no explicit "specific sites" list yet — derive a stub from
    // siteId/siteName. When neither is set, treat as "all sites / at checkout".
    const groupSiteIds = Array.isArray(g.siteIds) && g.siteIds.length > 0
      ? g.siteIds
      : (g.siteId != null ? [g.siteId] : []);
    const siteLabel = groupSiteIds.length > 0
      ? groupSiteIds
          .map((sid) => siteNameOf(siteById.get(sid) || { id: sid, name: g.siteName }))
          .join(', ')
      : 'Assigned to checkout';

    return {
      raw: g,
      id,
      name:     g.name || g.talkGroupName || '—',
      team:     g.team || g.teamName || g.name || '—',
      sites:    siteLabel,
      siteIds:  groupSiteIds,
      members:  memberCounts.get(id) ?? 0,
    };
  }), [groups, memberCounts, siteById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const data = !q ? rows : rows.filter((r) =>
      [r.name, r.team, r.sites].some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
    return [...data].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (sort.asc ? 1 : -1) * (av - bv);
      return (sort.asc ? 1 : -1) * String(av ?? '').localeCompare(String(bv ?? ''));
    });
  }, [rows, query, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  /* -------- Selection ----------------------------------------------------- */
  const allChecked  = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* -------- CRUD ---------------------------------------------------------- */
  const handleAdd = () => {
    if (!orgId) {
      toast.warning('Select an organization first from the top-right site picker.');
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    const g = row.raw;
    const groupSiteIds = Array.isArray(g.siteIds) && g.siteIds.length > 0
      ? g.siteIds
      : (g.siteId != null ? [g.siteId] : []);
    const hasSpecific = groupSiteIds.length > 0 || g.singleSite === true;
    setEditing({
      talkGroupId: g.talkGroupId ?? g.id,
      name: g.name || '',
      iconUrl: g.iconUrl || '',
      broadcast: g.broadcast || false,
      siteRestriction:    hasSpecific ? 'specific' : 'all',
      siteActivationMode: groupSiteIds.length > 0 ? 'manual' : 'checkout',
      siteIds:            groupSiteIds,
    });
    setModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete talk group "${row.name}"?`)) return;
    try {
      await organizationService.deleteTalkGroup(row.id, orgId);
      toast.success('Talk group deleted');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete talk group');
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      // Map UI form → backend payload (TalkGroupCreate/UpdateRequest).
      const isSpecific = form.siteRestriction === 'specific';
      const isManual   = isSpecific && form.siteActivationMode === 'manual';
      const payload = {
        name: form.name.trim(),
        singleSite: isSpecific,
        broadcast: !!form.broadcast,
        siteId: isManual && form.siteIds.length > 0 ? form.siteIds[0] : null,
        // siteIds/iconUrl/activationMode are kept in state only — wire them
        // on the backend when a corresponding column exists.
      };
      if (editing) {
        await organizationService.updateTalkGroup(editing.talkGroupId, payload, orgId);
        toast.success('Talk group updated');
      } else {
        await organizationService.createTalkGroup({ ...payload, orgId });
        toast.success('Talk group created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save talk group');
    } finally {
      setSaving(false);
    }
  };

  /* -------- Render -------------------------------------------------------- */
  return (
    <>
      {/* Section toolbar */}
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search talk groups"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <img src={`${CHK_ICON}/search.svg`} alt="" className="cm-icon-img" />
          </button>
          <div className="sp-heading">
            <h1>Talk groups</h1>
            <div className="sp-subhead"><strong>{filtered.length}</strong> TOTAL</div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search talk groups..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add group
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {/* Empty / loading / table */}
      {!orgId ? (
        <div className="empty-state">
          <i className="fas fa-microphone"></i>
          <div>Select an organization from the top-right picker to view its talk groups.</div>
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="empty-state"><i className="fas fa-spinner fa-spin"></i><div>Loading talk groups…</div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-microphone"></i>
          <div>No talk groups yet{currentOrg ? ` for ${currentOrg.displayName || currentOrg.name}` : ''}. Click <strong>+ Add group</strong> to create one.</div>
        </div>
      ) : (
        <table className="data-table cm-table sp-table">
          <thead>
            <tr>
              <th className="cm-col-check">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              {COLUMNS.map((c) => (
                <th key={c.id}>
                  <button className="data-table-sort" onClick={() => toggleSort(c.id)}>
                    {c.label}
                    <i className={`fas fa-sort${sort.key === c.id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
                  </button>
                </th>
              ))}
              <th className="cm-col-actions" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleRow(r.id)}
                    aria-label={`Select ${r.name}`}
                  />
                </td>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td>
                  <span className="tg-team-pill">{r.team}</span>
                </td>
                <td className="tg-sites-cell">{r.sites}</td>
                <td>{r.members}</td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit talk group"
                    aria-label="Edit talk group"
                    onClick={() => handleEdit(r)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete talk group"
                    aria-label="Delete talk group"
                    onClick={() => handleDelete(r)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TalkGroupModal
        open={modalOpen}
        initial={editing}
        sites={sites}
        saving={saving}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
    </>
  );
}

export default TalkGroupsPage;


